import { ReactNode, isValidElement, Children } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Renders GoToBuddy's structured markdown as a dark-themed editorial briefing.
 * Expected section headings (H2): INTRO, TOP 10 SNAPSHOT, MARKET CONTEXT,
 * TOKEN OBSERVATIONS, PORTFOLIO CONTEXT, EDITORIAL NOTE, DISCLAIMER.
 */

type SectionKey =
  | "intro"
  | "top10"
  | "market"
  | "tokens"
  | "portfolio"
  | "editorial"
  | "disclaimer"
  | "other";

function normalizeHeading(text: string): SectionKey {
  const t = text.trim().toLowerCase();
  if (t.startsWith("intro")) return "intro";
  if (t.startsWith("top 10")) return "top10";
  if (t.startsWith("market context")) return "market";
  if (t.startsWith("token observations")) return "tokens";
  if (t.startsWith("portfolio context")) return "portfolio";
  if (t.startsWith("editorial note")) return "editorial";
  if (t.startsWith("disclaimer")) return "disclaimer";
  return "other";
}

function getText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getText).join("");
  if (isValidElement(node)) return getText((node.props as { children?: ReactNode }).children);
  return "";
}

// Active section is tracked via a tiny module-level token because react-markdown
// renders nodes independently. We reset it per render via the key on the wrapper.
let currentSection: SectionKey = "other";

function changeBadgeClass(value: string): string {
  const num = parseFloat(value.replace(/[^\d.\-+]/g, ""));
  if (isNaN(num) || Math.abs(num) < 0.1) {
    return "bg-muted/40 text-muted-foreground";
  }
  if (num > 0) return "bg-emerald-500/15 text-emerald-400";
  return "bg-red-500/15 text-red-400";
}

function changeTextClass(value: string): string {
  const num = parseFloat(value.replace(/[^\d.\-+]/g, ""));
  if (isNaN(num) || Math.abs(num) < 0.1) return "text-muted-foreground";
  if (num > 0) return "text-emerald-400";
  return "text-red-400";
}

function observationClass(value: string): string {
  const v = value.trim().toLowerCase();
  if (v.includes("strong momentum")) return "bg-emerald-500/15 text-emerald-300";
  if (v.includes("positive")) return "bg-emerald-500/10 text-emerald-300/90";
  if (v.includes("weakening")) return "bg-red-500/15 text-red-300";
  if (v.includes("elevated")) return "bg-amber-500/15 text-amber-300";
  if (v.includes("watching")) return "bg-sky-500/10 text-sky-300";
  return "bg-muted/40 text-muted-foreground";
}

function TokenCards({ rows, headers }: { rows: string[][]; headers: string[] }) {
  const idx = (name: string) =>
    headers.findIndex((h) => h.toLowerCase().includes(name));
  const iToken = idx("token");
  const iPrice = idx("price");
  const i24 = idx("24");
  const i7 = idx("7");
  const iConv = idx("conviction");
  const iObs = idx("observation") >= 0 ? idx("observation") : idx("signal");
  const iReason = idx("key reason") >= 0 ? idx("key reason") : idx("reason");

  return (
    <div className="not-prose">
      {/* Desktop: aligned table */}
      <div className="hidden md:block -mx-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border/40 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              {iToken >= 0 && <th className="text-left font-medium px-2 py-2">Token</th>}
              {iPrice >= 0 && <th className="text-right font-medium px-2 py-2">Price</th>}
              {i24 >= 0 && <th className="text-right font-medium px-2 py-2">24h</th>}
              {i7 >= 0 && <th className="text-right font-medium px-2 py-2">7d</th>}
              {iConv >= 0 && <th className="text-right font-medium px-2 py-2">Conviction</th>}
              {iObs >= 0 && <th className="text-left font-medium px-2 py-2">Observation</th>}
              {iReason >= 0 && <th className="text-left font-medium px-2 py-2">Key Reason</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/20 hover:bg-muted/10 transition-colors">
                {iToken >= 0 && (
                  <td className="px-2 py-3 font-semibold text-foreground whitespace-nowrap">
                    {r[iToken]}
                  </td>
                )}
                {iPrice >= 0 && (
                  <td className="px-2 py-3 text-right tabular-nums text-foreground whitespace-nowrap">
                    {r[iPrice]}
                  </td>
                )}
                {i24 >= 0 && (
                  <td className={`px-2 py-3 text-right tabular-nums whitespace-nowrap ${changeTextClass(r[i24] || "")}`}>
                    {r[i24]}
                  </td>
                )}
                {i7 >= 0 && (
                  <td className={`px-2 py-3 text-right tabular-nums whitespace-nowrap ${changeTextClass(r[i7] || "")}`}>
                    {r[i7]}
                  </td>
                )}
                {iConv >= 0 && (
                  <td className="px-2 py-3 text-right font-mono text-xs text-muted-foreground whitespace-nowrap">
                    {(r[iConv] || "").trim()} / 10
                  </td>
                )}
                {iObs >= 0 && (
                  <td className="px-2 py-3 whitespace-nowrap">
                    {r[iObs] && (
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${observationClass(r[iObs])}`}>
                        {r[iObs]}
                      </span>
                    )}
                  </td>
                )}
                {iReason >= 0 && (
                  <td className="px-2 py-3 text-xs leading-[1.6] text-muted-foreground min-w-[220px]">
                    {r[iReason]}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: compact cards */}
      <div className="md:hidden space-y-3">
        {rows.map((r, i) => (
          <div
            key={i}
            className="rounded-lg border border-border/50 bg-card/30 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="font-semibold text-foreground text-base leading-tight min-w-0">
                {r[iToken]}
              </div>
              {iPrice >= 0 && (
                <div className="font-medium text-foreground text-base tabular-nums shrink-0">
                  {r[iPrice]}
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {i24 >= 0 && r[i24] && (
                <span className={`px-2 py-0.5 rounded font-medium tabular-nums ${changeBadgeClass(r[i24])}`}>
                  24h {r[i24]}
                </span>
              )}
              {i7 >= 0 && r[i7] && (
                <span className={`px-2 py-0.5 rounded font-medium tabular-nums ${changeBadgeClass(r[i7])}`}>
                  7d {r[i7]}
                </span>
              )}
              {iObs >= 0 && r[iObs] && (
                <span className={`px-2 py-0.5 rounded font-medium ${observationClass(r[iObs])}`}>
                  {r[iObs]}
                </span>
              )}
              {iConv >= 0 && r[iConv] && (
                <span className="ml-auto px-2 py-0.5 rounded bg-muted/40 text-muted-foreground font-mono">
                  {r[iConv].trim()}/10
                </span>
              )}
            </div>
            {iReason >= 0 && r[iReason] && (
              <div className="mt-2 text-xs leading-[1.6] text-muted-foreground">
                {r[iReason]}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const components: Components = {
  h1: ({ children }) => (
    <h1 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight text-foreground mt-2 mb-8">
      {children}
    </h1>
  ),
  h2: ({ children }) => {
    const text = getText(children);
    const key = normalizeHeading(text);
    currentSection = key;
    if (key === "intro") return null; // label is rendered as a paragraph below
    if (key === "disclaimer") return null;
    if (key === "editorial") return null;
    if (key === "portfolio") {
      return (
        <h2 className="mt-16 mb-4 text-sm font-medium text-muted-foreground">
          {text}
        </h2>
      );
    }
    return (
      <h2 className="mt-16 mb-5 text-xs md:text-sm font-bold uppercase tracking-[0.18em] text-foreground">
        {text}
      </h2>
    );
  },
  h3: ({ children }) => (
    <h3 className="mt-8 mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/80">
      {children}
    </h3>
  ),
  p: ({ children }) => {
    const text = getText(children).trim();
    // INTRO first paragraph: render as uppercase label line
    if (currentSection === "intro" && /—/.test(text) && text === text.toUpperCase()) {
      return (
        <p className="text-[11px] md:text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground mb-3">
          {text}
        </p>
      );
    }
    if (currentSection === "market") {
      return (
        <p className="border-l-2 border-primary/60 pl-5 my-4 text-foreground/85 text-base leading-[1.75]">
          {children}
        </p>
      );
    }
    if (currentSection === "editorial") {
      return (
        <div className="mt-10 rounded-md border border-border/40 bg-muted/20 px-5 py-4">
          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Editorial Note
          </div>
          <p className="italic text-sm leading-[1.7] text-foreground/75">
            {children}
          </p>
        </div>
      );
    }
    if (currentSection === "disclaimer") {
      return (
        <p className="mt-16 text-[11px] leading-[1.7] text-muted-foreground text-center max-w-[560px] mx-auto">
          {children}
        </p>
      );
    }
    if (currentSection === "portfolio") {
      return (
        <p className="text-[15px] leading-[1.7] text-foreground/85 my-3 opacity-90">
          {children}
        </p>
      );
    }
    return (
      <p className="text-base leading-[1.75] text-foreground/85 my-4">
        {children}
      </p>
    );
  },
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 space-y-2 my-4 text-[15px] leading-[1.7] text-foreground/85 opacity-90 marker:text-muted-foreground">
      {children}
    </ol>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 space-y-2 my-4 text-base leading-[1.7] text-foreground/85 marker:text-muted-foreground">
      {children}
    </ul>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  em: ({ children }) => <em className="italic">{children}</em>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
      {children}
    </a>
  ),
  table: ({ children }) => {
    // Parse the table structure into header + rows
    const headers: string[] = [];
    const rows: string[][] = [];
    Children.forEach(children, (group) => {
      if (!isValidElement(group)) return;
      const tag = (group.type as { name?: string })?.name || group.type;
      Children.forEach((group.props as { children?: ReactNode }).children, (tr) => {
        if (!isValidElement(tr)) return;
        const cells: string[] = [];
        Children.forEach((tr.props as { children?: ReactNode }).children, (cell) => {
          if (isValidElement(cell)) cells.push(getText((cell.props as { children?: ReactNode }).children));
        });
        if (tag === "thead" || cells.some((c) => headers.length === 0)) {
          if (headers.length === 0) headers.push(...cells);
          else rows.push(cells);
        } else {
          rows.push(cells);
        }
      });
    });
    if (!rows.length) return null;
    return <TokenCards headers={headers} rows={rows} />;
  },
  hr: () => <div className="my-10" />,
};

export function EditorialMarkdown({ content }: { content: string }) {
  // Reset section tracker for each render pass
  currentSection = "other";
  return (
    <div className="mx-auto w-full max-w-[780px] px-5 md:px-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{content}</ReactMarkdown>
    </div>
  );
}
